import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import throttle from 'lodash.throttle';
import styles from './my-account-tabs.module.scss';

export type TabItem = {
  headline: string;
  content: JSX.Element;
  id: string;
  disabled?: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

type TabPanelButtonType = {
  item: TabItem;
  isActive?: number;
  className: string;
  index: number;
};

export type MyAccountTabsProps = {
  items: TabItem[];
  activeTab?: number;
  className?: string;
};

const TabPanelButton: React.FC<TabPanelButtonType> = ({ index, isActive, item, className }) => {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const el = ref.current;

    if (isActive === index) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isActive, index]);

  return (
    <button
      ref={ref}
      id={`${item.id}-control`}
      aria-controls={`${item.id}-panel`}
      aria-selected={index === isActive}
      className={className}
      role="tab"
      onClick={item.onClick}
      disabled={item.disabled}
    >
      {item.headline}
    </button>
  );
};

export const MyAccountTabs: React.FC<MyAccountTabsProps> = ({ items, activeTab, className }) => {
  const [indicator, setIndicator] = useState({ left: false, right: false });
  const ref = useRef<HTMLDivElement>(null);

  const classes = classnames(styles.myAccountTabs, className, {
    [styles.gradientLeft]: indicator.left,
    [styles.gradientRight]: indicator.right
  });

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const el = ref.current;
    const hasLeftIndicator = el.scrollLeft > 0;
    const hasRightIndicator = el.scrollWidth - el.clientWidth > el.scrollLeft;

    setIndicator({ left: hasLeftIndicator, right: hasRightIndicator });
  }, []);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const el = ref.current;

    const handleIndicators = throttle(() => {
      const hasLeftIndicator = el.scrollLeft > 0;
      const hasRightIndicator = el.scrollWidth - el.clientWidth > el.scrollLeft;

      setIndicator({ left: hasLeftIndicator, right: hasRightIndicator });
    }, 400);

    el.addEventListener('scroll', handleIndicators, { passive: true });
    window.addEventListener('resize', handleIndicators, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleIndicators);
      window.removeEventListener('resize', handleIndicators);
    };
  });

  return (
    <div className={classes} data-testid="my-account-tabs-component">
      <div className={styles.tablistOuter}>
        <div role="tablist" className={styles.tablist} ref={ref}>
          {items.map((item, i) => {
            const buttonClass = classnames(styles.button, {
              [styles.active]: i === activeTab
            });

            return (
              <TabPanelButton
                index={i}
                isActive={activeTab}
                key={`${item.headline}-${i}-control`}
                className={buttonClass}
                item={item}
              />
            );
          })}
        </div>
      </div>
      {items.map((item, i) => {
        const panelClass = classnames(styles.panel, {
          [styles.active]: i === activeTab
        });

        return (
          <div
            aria-hidden={i !== activeTab}
            key={`${item.headline}-${i}-panel`}
            id={`${item.id}-panel`}
            role="tabpanel"
            aria-labelledby={`${item.id}-control`}
            className={panelClass}
          >
            {item.content}
          </div>
        );
      })}
    </div>
  );
};
